import { useState } from "react";
import { Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  PRODUCTS, canCheckout, checkoutErrorMessage, discountView, formatPrice, postCheckout, safeRedirectUrl,
  validateDiscountCode,
} from "@/lib/checkoutApi";
import { useSiteSettings } from "@/lib/siteSettings";

export const CHECKOUT_COPY = {
  de: {
    title: "Bestellung abschließen",
    vat: "inkl. MwSt.",
    email: "E-Mail-Adresse",
    emailHint: "Hierhin senden wir Bestätigung und Bericht.",
    code: "Rabattcode",
    apply: "Anwenden",
    codeInvalid: "Code ungültig",
    codeOk: (saved) => `Code angewendet: Sie sparen ${saved}`,
    terms: "Ich akzeptiere die",
    agb: "AGB",
    andPrivacy: "und habe die",
    privacy: "Datenschutzerklärung",
    read: "gelesen",
    waiverA: "Ich verlange ausdrücklich, dass Sie vor Ablauf der Widerrufsfrist mit der Leistung beginnen, und nehme zur Kenntnis, dass ich mein",
    waiverLink: "Widerrufsrecht",
    waiverB: "bei vollständiger Erbringung verliere.",
    pay: "Jetzt bezahlen",
    paying: "Weiterleitung zur Zahlung …",
  },
  en: {
    title: "Complete your order",
    vat: "incl. VAT",
    email: "E-mail address",
    emailHint: "We send the confirmation and the report here.",
    code: "Discount code",
    apply: "Apply",
    codeInvalid: "Code invalid",
    codeOk: (saved) => `Code applied: you save ${saved}`,
    terms: "I accept the",
    agb: "terms and conditions",
    andPrivacy: "and have read the",
    privacy: "privacy policy",
    read: "",
    waiverA: "I expressly request that you begin the service before the withdrawal period expires, and I acknowledge that I lose my",
    waiverLink: "right of withdrawal",
    waiverB: "once the service has been fully provided.",
    pay: "Pay now",
    paying: "Redirecting to payment …",
  },
};

// The form itself (no portal), so it can be rendered and tested on its own.
export function CheckoutForm({ token, product, lang = "de", onError }) {
  const C = CHECKOUT_COPY[lang] || CHECKOUT_COPY.de;
  const s = useSiteSettings();
  const base = product === "consulting" ? s.consulting_price_eur : s.report_price_eur;
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [applied, setApplied] = useState(null); // {valid, final, saved} | null
  const [checking, setChecking] = useState(false);
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [acceptWaiver, setAcceptWaiver] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const apply = async () => {
    if (!code.trim() || checking) return;
    setChecking(true);
    try {
      setApplied(discountView(base, await validateDiscountCode(code, product)));
    } catch (e) {
      const status = e && e.response && e.response.status;
      setApplied(status === 429 ? null : { valid: false, final: base, saved: 0 });
      if (status === 429) setError(checkoutErrorMessage(e, { product, lang }));
    } finally {
      setChecking(false);
    }
  };

  const shown = applied && applied.valid ? applied.final : base;
  const ok = canCheckout({ email, acceptTerms, acceptWaiver, busy });

  const submit = async (e) => {
    e.preventDefault();
    if (!ok) return;
    setBusy(true);
    setError("");
    try {
      const res = await postCheckout(token, { product, email, discountCode: applied && applied.valid ? code : "" });
      const url = safeRedirectUrl(res && res.url);
      if (!url) throw new Error("unsafe or missing checkout url");
      window.location.assign(url);
    } catch (err) {
      const msg = checkoutErrorMessage(err, { product, lang });
      setError(msg);
      if (onError) onError(msg);
      setBusy(false);
    }
    // On success the page navigates away; busy stays true so nothing is re-sent.
  };

  const link = "underline underline-offset-2 hover:text-primary";
  return (
    <form onSubmit={submit} className="space-y-4" data-testid="checkout-form" noValidate>
      <div className="rounded-lg border bg-accent/40 p-3">
        <p className="text-sm text-muted-foreground" data-testid="checkout-product">{PRODUCTS[product].name[lang] || PRODUCTS[product].name.de}</p>
        <p className="mt-0.5 flex flex-wrap items-baseline gap-x-2">
          {applied && applied.valid && (
            <span className="text-base text-muted-foreground line-through">{formatPrice(base, lang)}</span>
          )}
          <span className="text-3xl font-bold tabular-nums" data-testid="checkout-price">{formatPrice(shown, lang)}</span>
          <span className="text-sm text-muted-foreground">{C.vat}</span>
        </p>
      </div>

      <div className="space-y-1.5">
        <label htmlFor="co-email" className="text-sm font-medium">{C.email}</label>
        <Input
          id="co-email" type="email" autoComplete="email" inputMode="email" maxLength={200}
          value={email} onChange={(e) => setEmail(e.target.value)} data-testid="checkout-email"
        />
        <p className="text-xs text-muted-foreground">{C.emailHint}</p>
      </div>

      <div className="space-y-1.5">
        <label htmlFor="co-code" className="text-sm font-medium">{C.code}</label>
        <div className="flex gap-2">
          <Input
            id="co-code" maxLength={64} value={code} autoComplete="off"
            onChange={(e) => { setCode(e.target.value); setApplied(null); }} data-testid="checkout-code"
          />
          <Button type="button" variant="outline" onClick={apply} disabled={!code.trim() || checking} data-testid="checkout-apply">
            {checking ? <Loader2 className="h-4 w-4 animate-spin" /> : C.apply}
          </Button>
        </div>
        {applied && !applied.valid && <p className="text-sm text-red-600" data-testid="checkout-code-invalid">{C.codeInvalid}</p>}
        {applied && applied.valid && (
          <p className="text-sm text-green-700" data-testid="checkout-code-ok">{C.codeOk(formatPrice(applied.saved, lang))}</p>
        )}
      </div>

      <div className="space-y-3 text-sm">
        <div className="flex items-start gap-2.5">
          <Checkbox id="co-terms" checked={acceptTerms} onCheckedChange={(v) => setAcceptTerms(v === true)} className="mt-0.5" data-testid="checkout-terms" />
          <label htmlFor="co-terms" className="leading-snug">
            {C.terms} <a href="/agb" target="_blank" rel="noreferrer" className={link}>{C.agb}</a> {C.andPrivacy}{" "}
            <a href="/datenschutz" target="_blank" rel="noreferrer" className={link}>{C.privacy}</a> {C.read}
          </label>
        </div>
        <div className="flex items-start gap-2.5">
          <Checkbox id="co-waiver" checked={acceptWaiver} onCheckedChange={(v) => setAcceptWaiver(v === true)} className="mt-0.5" data-testid="checkout-waiver" />
          <label htmlFor="co-waiver" className="leading-snug">
            {C.waiverA} <a href="/widerruf" target="_blank" rel="noreferrer" className={link}>{C.waiverLink}</a> {C.waiverB}
          </label>
        </div>
      </div>

      {error && <p role="alert" className="text-sm text-red-600" data-testid="checkout-error">{error}</p>}

      <Button type="submit" size="lg" className="w-full" disabled={!ok} data-testid="checkout-pay">
        {busy ? <><Loader2 className="h-4 w-4 animate-spin" /> {C.paying}</> : C.pay}
      </Button>
    </form>
  );
}

export default function CheckoutDialog({ open, onOpenChange, token, product, lang = "de" }) {
  const C = CHECKOUT_COPY[lang] || CHECKOUT_COPY.de;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[92vh] overflow-y-auto sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{C.title}</DialogTitle>
          <DialogDescription className="sr-only">{PRODUCTS[product].name[lang] || PRODUCTS[product].name.de}</DialogDescription>
        </DialogHeader>
        {open && <CheckoutForm token={token} product={product} lang={lang} />}
      </DialogContent>
    </Dialog>
  );
}
